/**
 * CandidateNotes — talent-level notes rendered at the bottom of the candidate
 * meta-CV.
 *
 * The candidate is linked to a per-tenant Talent Pool profile
 * (`talentPoolProfileId`). Notes live on that profile, so this reuses the exact
 * same TanStack Query cache entry (`talentKeys.detail(profileId)`) and mutation
 * (`useAddComment`) as the Talent detail page: adding a note here refreshes the
 * Talent page and vice-versa (single source of truth, always in sync).
 *
 * Renders nothing when the candidate has no linked profile (e.g. legacy
 * CV-upload candidates not yet promoted into the Talent Pool).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useTalent } from '@/hooks/useTalentPool';
import { useAddComment } from '@/hooks/useTalentMutations';
import { TalentNotesSection } from '@/components/talent-pool/detail/TalentNotesSection';

interface CandidateNotesProps {
  talentPoolProfileId?: string;
}

export const CandidateNotes: React.FC<CandidateNotesProps> = ({ talentPoolProfileId }) => {
  const { t } = useTranslation('talentPool');
  const { data: talent, isLoading } = useTalent(talentPoolProfileId);
  const addCommentMutation = useAddComment(talentPoolProfileId ?? '');

  // No linked Talent Pool profile → no talent-level notes thread to show.
  if (!talentPoolProfileId) return null;

  return (
    <div className="space-y-4">
      <h5 className="w-full">{t('section.notes')}</h5>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : (
        <TalentNotesSection
          comments={talent?.comments ?? []}
          onAddComment={(body) => addCommentMutation.mutate({ body })}
          isPending={addCommentMutation.isPending}
        />
      )}
    </div>
  );
};

CandidateNotes.displayName = 'CandidateNotes';
